import type {
  RbacAccessAdminsPage,
  RbacAccessAdminsParams,
  RbacAccessOverview,
  RbacAdminPermissionUsageReport,
  RbacRoleFamily,
} from '@gozoki/api-types'
import { queryOptions, skipToken } from '@tanstack/react-query'
import { gozokiApiWithAuth } from '../gozokiApi'
import { keys } from './keys'

/**
 * Lectures de l'écran « Utilisateurs & accès » (spec §8.2).
 *
 * Les trois endpoints sont **serveur-autoritaires** : aucun agrégat, aucune alerte et aucune page
 * ne se recalculent dans le navigateur, pour que deux écrans ne comptent jamais différemment
 * « comptes sans rôle » le Jour J (§8.3).
 */

export const fetchRbacAccessOverview = async (usageWindowDays?: number) => {
  const response = await gozokiApiWithAuth.get<RbacAccessOverview>('/rbac/overview', {
    params: usageWindowDays === undefined ? undefined : { usageWindowDays },
  })
  return response.data
}

export const fetchRbacAccessAdmins = async (params: RbacAccessAdminsParams) => {
  const response = await gozokiApiWithAuth.get<RbacAccessAdminsPage>('/rbac/admins', { params })
  return response.data
}

export const fetchRbacRoleFamilies = async () => {
  const response = await gozokiApiWithAuth.get<RbacRoleFamily[]>('/rbac/families')
  return response.data
}

export const getRbacAccessOverviewOptions = (usageWindowDays?: number) =>
  queryOptions({
    queryKey: keys.rbac.overview(usageWindowDays),
    queryFn: () => fetchRbacAccessOverview(usageWindowDays),
  })

export const getRbacAccessAdminsOptions = (params: RbacAccessAdminsParams) =>
  queryOptions({
    queryKey: keys.rbac.admins(params),
    queryFn: () => fetchRbacAccessAdmins(params),
  })

export const getRbacRoleFamiliesOptions = () =>
  queryOptions({
    queryKey: keys.rbac.families(),
    queryFn: fetchRbacRoleFamilies,
  })

/**
 * Revue des habilitations d'un compte (`GET /rbac/usage/admins/:id`, lot F10) : un droit effectif
 * par ligne, avec son premier et son dernier usage. Aucun écran ne la consommait jusqu'ici.
 */
export const fetchRbacAdminPermissionUsage = async (adminId: number, days?: number) => {
  const response = await gozokiApiWithAuth.get<RbacAdminPermissionUsageReport>(
    `/rbac/usage/admins/${adminId}`,
    { params: days === undefined ? undefined : { days } },
  )
  return response.data
}

export const getRbacAdminPermissionUsageOptions = (adminId: number | undefined, days?: number) =>
  queryOptions({
    queryKey: keys.rbac.adminUsage(adminId, days),
    queryFn: adminId === undefined ? skipToken : () => fetchRbacAdminPermissionUsage(adminId, days),
  })
